import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type * as monaco from 'monaco-editor';
import type { CodeSnippetImport } from '../types';
import type { FrameworkAction, FrameworkArgument, FrameworkClass } from '../../../framework/framework.d';
import { FrameworkClassType } from '../../../framework/framework-types';
import { getEnumOptions } from '../../../framework/framework-enums';
import FrameworkActionArgumentFields, { type DriveUnitPortArgFieldContext } from '../../Drawers/Controller/FrameworkActionArgumentFields';
import {
  isFrameworkNumberArgType,
  isDriveUnitPortFrameworkArgument,
  isPlainFrameworkNumericLiteral,
  frameworkArgValueToFormString,
  shouldPreserveRawDriveUnitPortArgument,
  shouldPreserveRawFrameworkEnumArgument,
  shouldPreserveRawFrameworkNumericArgument,
  shouldPreserveRawFrameworkStringArgument,
} from '../../../utils/frameworkArgTypes';
import { collectElectronicVariableNamesForClass, isFrameworkElectronicClassArgType } from '../../../utils/frameworkClassNameSets';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { useDispatch } from 'react-redux';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { isDriveUnitPortTakenElsewhere } from '../../../utils/driveUnitPortConflict';
import { DRIVE_UNIT_PORT_MAX, DRIVE_UNIT_PORT_MIN } from '../../../utils/driveUnitPortSelection';
import { escapePythonStringLiteral, shouldQuotePythonActionArg } from '../../../utils/pythonActionFormatting';
import {
  defaultSequenceTupleRowArgValues,
  isSequenceTupleArg,
  isTupleArg,
  readTupleMemberValue,
  sequenceTupleMemberValueKey,
  sequenceTupleRowIndices,
  tupleMemberSchemaDefault,
  tupleMemberValueKey,
} from '../../../utils/tupleArg';
import { scalarFrameworkArgIsValid, sequenceTupleRowsAreValid } from '../../../utils/sequenceTupleValidation';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import { replaceConstructorKwargInCode } from '../../../utils/pythonSourceScanUtils';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';
import { resolveAutoZeroSensorAction } from '../../../utils/autoZeroSensorActionArgs';
import { formatActionExpression, removeActionArgValues } from '../ConfigurationTabs/driveModeAutonomousActionUtils';

const isFollowPathAction = (name: string | undefined): boolean => {
  return name === 'follow_path_action';
};

const formatActionArgumentValue = (arg: FrameworkArgument, value: string, actionName: string): string => {
  const shouldQuote = shouldQuotePythonActionArg(arg, actionName);
  const isNumber = isFrameworkNumberArgType(arg.type || '');

  if (value === '') {
    return '';
  }
  if (
    shouldPreserveRawDriveUnitPortArgument(arg, value) ||
    shouldPreserveRawFrameworkNumericArgument(arg, value) ||
    shouldPreserveRawFrameworkEnumArgument(arg, value) ||
    shouldPreserveRawFrameworkStringArgument(arg, value, actionName)
  ) {
    return value;
  }
  if (isNumber && isPlainFrameworkNumericLiteral(value)) {
    return value;
  }
  if (!shouldQuote && /^(True|False|None)$/i.test(value)) {
    const v = value.toLowerCase();
    return v === 'true' ? 'True' : v === 'false' ? 'False' : 'None';
  }
  if (!shouldQuote && /^[A-Za-z_][\w]*(\.[A-Za-z_][\w]*)*$/.test(value)) {
    return value;
  }
  return `"${escapePythonStringLiteral(value)}"`;
};

export interface PendingActionDrop {
  /** Localized snippet title used for completion announcements. */
  snippetTitle?: string;
  /** The action label, e.g. "swerve.drive_for_time" */
  actionLabel: string;
  /** The code template with empty args, e.g. "swerve.drive_for_time(duration=, vx=, vy=)" */
  code: string;
  /** Monaco snippet text aligned with `code`, when the insertion has tab stops. */
  insertText?: string;
  /** Dependency code inserted only after the dialog is confirmed. */
  supportingCode?: string;
  /** Line number to insert at */
  targetLine: number;
  /** The framework action definition with argument metadata */
  actionDef: FrameworkAction;
  /** Imports required by the completed action snippet. */
  imports?: CodeSnippetImport[];
  /** Distinguishes input mappings from ordinary RobotBase actions. */
  kind?: 'action' | 'input-mapping';
  /** Generated source arguments that should not be edited as scalar form values. */
  hiddenArgumentNames?: string[];
}

interface ActionArgumentsDialogProps {
  open: boolean;
  onClose: () => void;
  pendingDrop: PendingActionDrop | null;
  pathOptions: Array<{ id: string; name: string }>;
  onConfirm: (filledCode: string, filledInsertText?: string) => void;
  editor?: monaco.editor.IStandaloneCodeEditor | null;
  driveUnitPortRegistryRev?: number;
}

const PENDING_ACTION_DROP_SYNTH_PREFIX = '__pending_action_drop__';

const replaceActionKeywordArgument = (code: string, argName: string, formattedValue: string): string => {
  const replacedExistingValue = replaceConstructorKwargInCode(code, 0, argName, formattedValue);
  if (replacedExistingValue !== code) return replacedExistingValue;

  const emptyPlaceholderPattern = new RegExp(`(${argName}\\s*=\\s*)(?=[,)])`);
  return code.replace(emptyPlaceholderPattern, `$1${formattedValue}`);
};

const pendingArgStubMechanismClass: FrameworkClass = {
  name: '__PendingActionArgStub',
  type: FrameworkClassType.Electronic,
  uiPanel: null,
  children: [],
  initialization: [],
};

const ActionArgumentsDialog = ({
  open,
  onClose,
  pendingDrop,
  pathOptions,
  onConfirm,
  editor = null,
  driveUnitPortRegistryRev = 0,
}: ActionArgumentsDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [argValues, setArgValues] = useState<Record<string, string>>({});
  const enumOptions = useMemo(() => getEnumOptions(), []);
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const programCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const activeSyntheticElectronicsRef = useRef<Set<string>>(new Set());
  const conflictMessage = t('portSelector.conflict.message', {
    defaultValue: 'This port is already used by another mechanism or electronic in your code.',
  });
  const editableActionDef = useMemo(() => {
    if (!pendingDrop?.actionDef) return undefined;
    const hiddenArgumentNames = new Set(pendingDrop.hiddenArgumentNames ?? []);
    return {
      ...pendingDrop.actionDef,
      arguments: (pendingDrop.actionDef.arguments ?? []).filter((arg) => !hiddenArgumentNames.has(arg.name)),
    };
  }, [pendingDrop]);
  const effectiveActionDef = useMemo(() => withResolvedMotorVelocityLimits(editableActionDef, argValues), [editableActionDef, argValues]);
  const autoZeroSensorResolution = useMemo(
    () => resolveAutoZeroSensorAction(effectiveActionDef, argValues, programCode, { availableBeforeLineNumber: pendingDrop?.targetLine }),
    [effectiveActionDef, argValues, programCode, pendingDrop?.targetLine]
  );
  const displayedActionDef = autoZeroSensorResolution?.actionDef ?? effectiveActionDef;

  const driveUnitPortArgFieldContext: DriveUnitPortArgFieldContext | null = useMemo(() => {
    if (!open || !pendingDrop?.actionLabel) return null;
    const syntheticIdPrefix = `${PENDING_ACTION_DROP_SYNTH_PREFIX}:${pendingDrop.actionLabel}`;
    const getOccupiedPortInlineMessage = (argName: string) => {
      const raw = argValues[argName] ?? '';
      const frameworkArg = (editableActionDef?.arguments ?? []).find((arg) => arg.name === argName);
      if (frameworkArg && shouldPreserveRawDriveUnitPortArgument(frameworkArg, raw)) return null;
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed < DRIVE_UNIT_PORT_MIN || parsed > DRIVE_UNIT_PORT_MAX) return null;
      const electronicKey = `${syntheticIdPrefix}:${argName}`;
      if (!isDriveUnitPortTakenElsewhere(driveUnitPorts, parsed, electronicKey)) return null;
      return conflictMessage;
    };
    return {
      editor,
      lineNumber: pendingDrop.targetLine,
      syntheticIdPrefix,
      driveUnitPortRegistryRev,
      stubMechanismClass: pendingArgStubMechanismClass,
      portConflictPresentation: 'inline',
      getOccupiedPortInlineMessage,
    };
  }, [open, pendingDrop, editableActionDef, editor, driveUnitPortRegistryRev, driveUnitPorts, argValues, conflictMessage]);

  useEffect(() => {
    if (!open) {
      for (const electronic of activeSyntheticElectronicsRef.current) {
        dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic }));
      }
      activeSyntheticElectronicsRef.current = new Set();
      return;
    }

    if (!pendingDrop || !editableActionDef) return;

    const syntheticIdPrefix = `${PENDING_ACTION_DROP_SYNTH_PREFIX}:${pendingDrop.actionLabel}`;
    const portArgs = (editableActionDef.arguments ?? []).filter((arg) => isDriveUnitPortFrameworkArgument(arg));
    const nextActive = new Set<string>();

    for (const arg of portArgs) {
      const electronic = `${syntheticIdPrefix}:${arg.name}`;
      nextActive.add(electronic);
      const raw = argValues[arg.name] ?? '';
      if (shouldPreserveRawDriveUnitPortArgument(arg, raw)) {
        dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic }));
        continue;
      }
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed < DRIVE_UNIT_PORT_MIN || parsed > DRIVE_UNIT_PORT_MAX) {
        dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic }));
        continue;
      }
      dispatch(applicationSlice.actions.onUpdateDriveUnitPorts({ driveUnitPort: parsed, electronic }));
    }

    for (const prev of activeSyntheticElectronicsRef.current) {
      if (!nextActive.has(prev)) {
        dispatch(applicationSlice.actions.onRemoveDriveUnitPortOccupancy({ electronic: prev }));
      }
    }
    activeSyntheticElectronicsRef.current = nextActive;
  }, [open, pendingDrop, editableActionDef, argValues, dispatch, driveUnitPortRegistryRev]);

  // Seed arg defaults when the dialog opens (or the pending action changes).
  // Intentionally omit `programCode` / live pathOptions churn from deps so Redux
  // code updates while the dialog is open do not wipe in-progress edits.
  useEffect(() => {
    if (!open || !pendingDrop?.actionDef || !editableActionDef) {
      setArgValues({});
      return;
    }

    const args = editableActionDef.arguments || [];
    const initialValues: Record<string, string> = {};

    const seedScalar = (arg: FrameworkArgument, key: string, valueOverride?: unknown) => {
      const defaultVal = valueOverride ?? arg.defaultValue ?? arg.value;
      const enumOpts = isFrameworkNumberArgType(arg.type || '') ? undefined : enumOptions[arg.type || ''];

      if (defaultVal !== undefined && defaultVal !== null && String(defaultVal) !== '') {
        initialValues[key] = frameworkArgValueToFormString(arg, defaultVal);
      } else if (enumOpts && enumOpts.length > 0) {
        initialValues[key] = enumOpts[0].value;
      } else if (isFollowPathAction(pendingDrop.actionDef.name) && arg.name === 'path' && pathOptions.length > 0) {
        initialValues[key] = pathOptions[0].name;
      } else if (isFrameworkElectronicClassArgType(arg.type)) {
        const electronicOptions = collectElectronicVariableNamesForClass(programCode, arg.type || '', {
          availableBeforeLineNumber: pendingDrop.targetLine,
        });
        initialValues[key] = electronicOptions[0] ?? '';
      } else {
        initialValues[key] = '';
      }
    };

    args.forEach((arg) => {
      const frameworkArg = arg as FrameworkArgument;
      if (isSequenceTupleArg(frameworkArg)) {
        // Seed one editable row so the user isn't faced with an empty, non-editable field.
        Object.assign(initialValues, defaultSequenceTupleRowArgValues(frameworkArg));
        return;
      }
      if (isTupleArg(frameworkArg)) {
        (frameworkArg.members || []).forEach((member, idx) => {
          const memberArg = member as FrameworkArgument;
          const stored = readTupleMemberValue(frameworkArg, idx, memberArg.name);
          const schemaDefault = tupleMemberSchemaDefault(frameworkArg, idx, memberArg);
          seedScalar(memberArg, tupleMemberValueKey(frameworkArg.name, memberArg.name), stored ?? schemaDefault);
        });
        return;
      }
      seedScalar(frameworkArg, frameworkArg.name);
    });

    setArgValues(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed only on open / action change; snapshot programCode + pathOptions at that time
  }, [open, pendingDrop?.actionDef, editableActionDef, enumOptions]);

  const handleArgChange = (name: string, value: string) => {
    setArgValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleConfirm = () => {
    if (!pendingDrop) return;

    if (autoZeroSensorResolution) {
      const serializedValues = removeActionArgValues(argValues, autoZeroSensorResolution.inapplicableArgumentNames);
      const filled = formatGeneratedPythonCall(
        formatActionExpression(pendingDrop.actionLabel, serializedValues, () => autoZeroSensorResolution.actionDef)
      );
      if (pendingDrop.insertText === undefined) {
        onConfirm(filled);
      } else {
        onConfirm(filled, `${filled}$0`);
      }
      return;
    }

    // Build the filled code by replacing empty argument placeholders
    let filled = pendingDrop.code;
    let filledInsertText = pendingDrop.insertText;
    const args = editableActionDef?.arguments || [];

    args.forEach((arg) => {
      const frameworkArg = arg as FrameworkArgument;
      let formattedValue: string;

      if (isSequenceTupleArg(frameworkArg)) {
        const members = frameworkArg.members || [];
        const rows = sequenceTupleRowIndices(frameworkArg.name, argValues).map((rowIndex) => {
          const memberValues = members.map((member) => {
            const memberArg = member as FrameworkArgument;
            return formatActionArgumentValue(
              memberArg,
              argValues[sequenceTupleMemberValueKey(frameworkArg.name, rowIndex, memberArg.name)] ?? '',
              pendingDrop.actionDef.name
            );
          });
          return `(${memberValues.join(', ')})`;
        });
        formattedValue = `[${rows.join(', ')}]`;
      } else if (isTupleArg(frameworkArg)) {
        const members = frameworkArg.members || [];
        const memberValues = members.map((member) => {
          const memberArg = member as FrameworkArgument;
          return formatActionArgumentValue(
            memberArg,
            argValues[tupleMemberValueKey(frameworkArg.name, memberArg.name)] ?? '',
            pendingDrop.actionDef.name
          );
        });
        formattedValue = `(${memberValues.join(', ')}${memberValues.length === 1 ? ',' : ''})`;
      } else {
        formattedValue = formatActionArgumentValue(frameworkArg, argValues[frameworkArg.name] ?? '', pendingDrop.actionDef.name);
      }

      filled = replaceActionKeywordArgument(filled, arg.name, formattedValue);
      if (filledInsertText) {
        filledInsertText = replaceActionKeywordArgument(filledInsertText, arg.name, formattedValue);
      }
    });

    if (filledInsertText === undefined) {
      onConfirm(filled);
    } else {
      onConfirm(filled, filledInsertText);
    }
  };

  // Check if all required fields are filled
  const canConfirm = useMemo(() => {
    if (!displayedActionDef?.arguments) return true;
    return displayedActionDef.arguments.every((arg) => {
      const frameworkArg = arg as FrameworkArgument;
      if (isSequenceTupleArg(frameworkArg)) {
        return sequenceTupleRowsAreValid(frameworkArg, argValues, t as TFunction);
      }
      if (isTupleArg(frameworkArg)) {
        return (frameworkArg.members || []).every((member) => {
          return scalarFrameworkArgIsValid(
            member as FrameworkArgument,
            tupleMemberValueKey(frameworkArg.name, member.name),
            argValues,
            t as TFunction
          );
        });
      }
      return scalarFrameworkArgIsValid(frameworkArg, frameworkArg.name, argValues, t as TFunction);
    });
  }, [displayedActionDef, argValues, t]);

  // Extract action name for display
  const actionDisplayName = useMemo(() => {
    if (!pendingDrop?.actionLabel) return '';
    const parts = pendingDrop.actionLabel.split('.');
    return parts[parts.length - 1] || pendingDrop.actionLabel;
  }, [pendingDrop?.actionLabel]);
  const pendingTargetLine = pendingDrop?.targetLine;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {pendingDrop?.kind === 'input-mapping'
          ? t('codeEditor.inputMappingArgs.title', 'Configure input mapping')
          : t('codeEditor.actionArgs.title', 'Configure Action')}
      </DialogTitle>
      <DialogContent>
        {effectiveActionDef && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              {actionDisplayName}
            </Typography>
            <FrameworkActionArgumentFields
              actionDef={effectiveActionDef}
              values={argValues}
              onArgChange={handleArgChange}
              onArgValuesChange={setArgValues}
              pathOptions={pathOptions}
              enumOptions={enumOptions}
              t={t as TFunction}
              layout="row"
              showUnits
              spacing={1.5}
              idPrefix="drop-arg"
              availableBeforeLineNumber={pendingTargetLine}
              driveUnitPortArgFieldContext={driveUnitPortArgFieldContext}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button variant="contained" disabled={!canConfirm} onClick={handleConfirm}>
          {t('common.ok', 'OK')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActionArgumentsDialog;
